import Table from "@/common/components/Table"
import { Pagination } from "@/common/components/pagination"
import { KeyValueObj } from "@/common/types"
import { useState, useMemo } from "react"
import { Button } from "@/common/components/button"
import { ClusterWrapper, ModalWrapper } from "./style"
import { TableHeaders, TableKeys, TableData } from "./sub/data"
import useQuery from "@/hooks/useQuery"
import { ADMIN_CLUSTERS } from "@/common/constants"
import { Modal } from "@/common/components/modal"
import success from "@/assets/images/success_alt.png"
import { useApiCallHandling } from "@/hooks/useApiCallHandling"
import { useNotification } from "@/common/components/Notifications/NotificationProvider"
import ViewClusterModal from "./sub/view-cluster-modal"
import EditClusterModal from "./sub/edit-cluster-modal"
import AddClusterModal from "./sub/add-cluster-modal"
import { ActionPopup } from "@/common/components/action-popup"
import { usePositioning } from "@/hooks/usePositioning"

enum EModal {
  ADD_CLUSTER = "add_cluster",
  EDIT_CLUSTER = "edit_cluster",
  SUCCESS = "success",
  VIEW_CLUSTER = "view_cluster",
}

export default function AdminCluster() {
  const [activeModal, setActiveModal] = useState<EModal | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [selectedCluster, setSelectedCluster] = useState<any>(null)
  const [statusToggle, setStatusToggle] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [setCordinate, cordinate] = usePositioning()

  const [pagination, setPagination] = useState<KeyValueObj>({
    page: 1,
    per_page: 10,
  })

  const makeRequest = useApiCallHandling()
  const notify = useNotification()

  const {
    data: clusterResponse,
    loading: clustersLoading,
    meta: clustersMeta,
    refetch: refetchClusters,
  } = useQuery(ADMIN_CLUSTERS + `?page=${pagination.page}&per_page=${pagination.per_page}`)

  const clusters = clusterResponse?.data || clusterResponse || []

  const handleDropdownClick = (e: any, rowId: string | number) => {
    setCordinate(e)
    const cluster = clusters.find((c: any) => c.id === rowId)
    setSelectedCluster(cluster || null)
    setShowActions(true)
  }

  const handleViewAction = () => {
    setActiveModal(EModal.VIEW_CLUSTER)
    setShowActions(false)
  }

  const handleEditAction = () => {
    if (selectedCluster) {
      setStatusToggle(selectedCluster?.status?.toLowerCase() === "active")
    }
    setActiveModal(EModal.EDIT_CLUSTER)
    setShowActions(false)
  }

  const handleSubmit = (formValues: any) => {
    setSubmitLoading(true)

    makeRequest(
      ADMIN_CLUSTERS,
      "POST",
      formValues,
      (_) => {
        setActiveModal(EModal.SUCCESS)
        refetchClusters()
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error creating cluster",
          type: "ERROR",
        })
      },
      () => {
        setSubmitLoading(false)
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    )
  }

  const handleEdit = (formValues: any) => {
    setSubmitLoading(true)
    const payload = {
      ...formValues,
      status: statusToggle ? "active" : "inactive",
    }

    makeRequest(
      `${ADMIN_CLUSTERS}/${selectedCluster?.id}`,
      "PUT",
      payload,
      (_) => {
        setActiveModal(EModal.SUCCESS)
        refetchClusters()
        setSelectedCluster(null)
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error updating cluster",
          type: "ERROR",
        })
      },
      () => {
        setSubmitLoading(false)
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    )
  }

  const tableDataWithActions = useMemo(() => {
    return TableData(clusters).map((row: any) => ({
      ...row,
      action: {
        type: "button",
        buttonType: "dots",
        buttonLoading: false,
        buttonName: "",
        onClick: handleDropdownClick,
      },
    }))
  }, [clusters])

  return (
    <ClusterWrapper>
      <div className="add-button">
        <Button onClick={() => setActiveModal(EModal.ADD_CLUSTER)}>Add Cluster</Button>
      </div>

      <ActionPopup
        position={cordinate}
        open={showActions}
        disabled={submitLoading}
        actions={[
          { label: "View", action: handleViewAction },
          { label: "Edit", action: handleEditAction },
        ]}
        onClose={() => setShowActions(false)}
      />

      <div className="table w-full">
        <Table
          tableHeaders={[...TableHeaders, ""]}
          tableData={tableDataWithActions}
          tableKeys={[...TableKeys, "action"]}
          tableTitle="Total Clusters"
          tableSubtitle={`${clustersMeta?.total_count ?? 0} clusters`}
          loading={clustersLoading}
          spaced
          rowLineColor="light"
        />

        <Pagination
          pageCount={clustersMeta?.total_pages || 1}
          onNextPage={(event) => setPagination({ ...pagination, page: event.selected + 1 })}
          onPerPageChange={(event) =>
            setPagination({
              ...pagination,
              per_page: Number(event?.target?.value),
              page: 1,
            })
          }
        />
      </div>

      <AddClusterModal
        isOpen={activeModal === EModal.ADD_CLUSTER}
        onClose={() => setActiveModal(null)}
        submitLoading={submitLoading}
        onSubmit={handleSubmit}
      />

      <ViewClusterModal
        isOpen={activeModal === EModal.VIEW_CLUSTER}
        onClose={() => {
          setActiveModal(null)
          setSelectedCluster(null)
        }}
        cluster={selectedCluster}
      />

      <EditClusterModal
        isOpen={activeModal === EModal.EDIT_CLUSTER}
        onClose={() => {
          setActiveModal(null)
          setSelectedCluster(null)
        }}
        cluster={selectedCluster}
        submitLoading={submitLoading}
        statusToggle={statusToggle}
        onStatusToggle={setStatusToggle}
        onSubmit={handleEdit}
      />

      <Modal isOpen={activeModal === EModal.SUCCESS} close={() => setActiveModal(null)}>
        <ModalWrapper>
          <img src={success} alt="success" />
          <p>Cluster Successfully {selectedCluster ? "Updated" : "Created"}</p>

          <Button onClick={() => setActiveModal(null)}>Done</Button>
        </ModalWrapper>
      </Modal>
    </ClusterWrapper>
  )
}
